import React, { useState, useEffect } from "react"
import * as XLSX from "xlsx"
import { manageDeviceBulkCreate } from "../../../../api/manage_device_bulk_create"
import { getManageEstateList } from "../../../../api/manage_estate_list"
import { ManageEstateData } from "../../estate"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"
import { getContractInformation } from "../../../../api/get_contract_information"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material"

interface ManageDeviceBulkCreateProps {
  onClose: (num: number) => void
}

const ManageDeviceBulkCreate: React.FC<ManageDeviceBulkCreateProps> = ({ onClose }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [estateId, setEstateId] = useState("")
  const [serialList, setSerialList] = useState<string[]>([])
  const [formatDownloadUrl, setFormatDownloadUrl] = useState<string>("")
  const [errors, setErrors] = useState({
    file: "",
    server: "",
    validation: false
  })

  useEffect(() => {
    const fetchEstateList = async () => {
      try {
        startLoading()
        const data = await getManageEstateList()
        setEstateData(data.estate_list)

        //契約時資料のダウンロードリンク
        const contractInformation = await getContractInformation()
        setFormatDownloadUrl(contractInformation.url_bulk_device)
      } catch (error: any) {
        if (error.response?.status === 401) navigate("/login")
      } finally {
        stopLoading()
      }
    }
    fetchEstateList()
  }, [])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 【重要】同じファイル名を再度選択しても onChange が発火するようにリセットする
    e.target.value = ""

    // 読み込み前に古いデータとエラーをクリアする（必要であれば）
    setSerialList([])
    setErrors((prev) => ({ ...prev, file: "", server: "" }))

    startLoading()
    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result
        const wb = XLSX.read(bstr, { type: "binary" })
        const wsName = wb.SheetNames[0]
        const ws = wb.Sheets[wsName]

        const extractedSerials: string[] = []
        for (let r = 3; r < 53; r++) {
          const cellRef = XLSX.utils.encode_cell({ c: 1, r: r })
          const cell = ws[cellRef]
          const val = cell ? String(cell.v).trim() : ""
          if (val === "") break
          extractedSerials.push(val)
        }

        if (extractedSerials.length === 0) {
          setErrors((prev) => ({ ...prev, file: "シリアルNoが読み取れませんでした（B4セル以降）" }))
        } else {
          setSerialList(extractedSerials)
          setErrors((prev) => ({ ...prev, file: "" }))
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, file: "Excelの解析に失敗しました" }))
      } finally {
        stopLoading()
      }
    }
    reader.readAsBinaryString(file)
  }

  // 個別のシリアルNoバリデーション
  const validateSerial = (sn: string) => {
    const alphanumericRegex = /^[a-zA-Z0-9]+$/
    if (sn.length !== 12) return "12文字で入力してください"
    if (!alphanumericRegex.test(sn)) return "英数字のみ"
    return null
  }

  const hasValidationError = serialList.some((sn) => validateSerial(sn) !== null)

  const handleSubmit = async () => {
    if (serialList.length === 0 || hasValidationError) return
    try {
      startLoading()
      await manageDeviceBulkCreate(serialList, estateId)
      onClose(1)
    } catch (error: any) {
      setErrors((prev) => ({ ...prev, server: "登録に失敗しました" }))
    } finally {
      stopLoading()
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card sx={{ width: "95%", maxWidth: "600px", p: 3, maxHeight: "90vh", overflowY: "auto" }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h4">デバイス一括追加</MDTypography>
          <button
            onClick={() => onClose(0)}
            style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer" }}
          >
            ×
          </button>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant="subtitle2" mb={1}>
            1. 不動産業者の選択（任意）
          </MDTypography>
          <select
            value={estateId}
            onChange={(e) => setEstateId(e.target.value)}
            style={{ width: "100%", padding: "8px" }}
          >
            <option value="">未選択（サービス提供元直属）</option>
            {estateData.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </MDBox>

        <MDBox mb={3}>
          {/* 4. タイトルとダウンロードリンクの並びを参考コードに合わせて調整 */}
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <MDTypography variant="subtitle2" style={{ margin: 0 }}>
              2. Excelファイルのアップロード
            </MDTypography>
            {formatDownloadUrl && (
              <a
                href={formatDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: "0.75rem", textDecoration: "none", color: "#1A73E8" }}
              >
                フォーマットをダウンロード
              </a>
            )}
          </MDBox>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} />
          {errors.file && (
            <MDTypography color="error" variant="caption" display="block">
              {errors.file}
            </MDTypography>
          )}
        </MDBox>

        {serialList.length > 0 && (
          <MDBox mb={3}>
            <MDTypography variant="subtitle2" mb={1}>
              3. 読み込み内容確認 ({serialList.length}件)
            </MDTypography>
            <TableContainer component={Paper} sx={{ maxHeight: "300px", boxShadow: "none", border: "1px solid #eee" }}>
              <Table stickyHeader sx={{ minWidth: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: "0.875rem",
                        color: "text.primary",
                        fontWeight: "bold",
                        backgroundColor: "#f8f9fa !important",
                        display: "table-cell" // 明示的に指定
                      }
                    }}
                  >
                    <TableCell align="center" style={{ width: "60px" }}>
                      No
                    </TableCell>
                    <TableCell align="left" style={{ width: "140px" }}>
                      シリアルNo
                    </TableCell>
                    <TableCell align="left" style={{ width: "auto" }}>
                      状態
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ display: "table-row-group" }}>
                  {serialList.map((sn, idx) => {
                    const err = validateSerial(sn)
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          display: "table-row", // 明示的に指定
                          "& .MuiTableCell-root": {
                            fontSize: "0.875rem",
                            color: "text.primary",
                            fontWeight: "normal",
                            display: "table-cell" // 明示的に指定
                          }
                        }}
                      >
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell align="left" sx={{ wordBreak: "break-all" }}>
                          {sn}
                        </TableCell>
                        <TableCell align="left">
                          {err ? (
                            <span style={{ color: "red", fontSize: "0.75rem", fontWeight: "bold" }}>{err}</span>
                          ) : (
                            <span style={{ color: "green", fontSize: "0.75rem" }}>OK</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        )}
        {errors.server && (
          <MDTypography color="error" variant="caption" mb={2} display="block">
            {errors.server}
          </MDTypography>
        )}

        <MDButton
          variant="contained"
          color="info"
          fullWidth
          disabled={serialList.length === 0 || hasValidationError}
          onClick={handleSubmit}
        >
          一括登録を実行
        </MDButton>
      </Card>
    </div>
  )
}

export default ManageDeviceBulkCreate
